import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, AlertCircle, Search, UserPlus, Check, X } from "lucide-react";

export function NewTrainingDialog({ open, onOpenChange, settings }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    trainee_user_id: "", trainee_name: "", training_start_date: today, trainee_position: "",
    training_type: "", location: "", manager: "", notes: "",
  });
  const [empQuery, setEmpQuery] = useState("");
  const [empResults, setEmpResults] = useState([]);
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [creatingEmp, setCreatingEmp] = useState(false);
  const [newEmp, setNewEmp] = useState({ name: "", email: "", position: "", location: "" });

  useEffect(() => {
    if (!open || form.trainee_user_id) return;
    let active = true;
    const t = setTimeout(async () => {
      try { const r = await api.get("/users/search", { params: { q: empQuery } }); if (active) setEmpResults(r.data); }
      catch { /* ignore */ }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [empQuery, open, form.trainee_user_id]);

  const selectEmp = (u) => {
    setForm((f) => ({ ...f, trainee_user_id: u.user_id, trainee_name: u.name, trainee_position: u.position || f.trainee_position }));
    setEmpResults([]); setShowAddEmp(false);
  };
  const clearEmp = () => setForm((f) => ({ ...f, trainee_user_id: "", trainee_name: "" }));
  const createEmp = async () => {
    if (!newEmp.name || !newEmp.email) { toast.error("Name and email required"); return; }
    setCreatingEmp(true);
    try {
      const u = (await api.post("/users/quick-create", newEmp)).data;
      selectEmp(u);
      setNewEmp({ name: "", email: "", position: "", location: "" });
      toast.success("Employee added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add employee"); }
    finally { setCreatingEmp(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = { ...form, training_start_date: new Date(form.training_start_date).toISOString() };
      return (await api.post("/training-sessions", payload)).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(`Training ${data.session_number} started`);
      onOpenChange(false);
      setForm({ trainee_user_id: "", trainee_name: "", training_start_date: today, trainee_position: "", training_type: "", location: "", manager: "", notes: "" });
      setEmpQuery(""); setEmpResults([]); setShowAddEmp(false);
      navigate(`/sessions/${data.id}`);
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Could not start training");
    },
  });

  const valid = form.trainee_user_id && form.training_start_date && form.trainee_position && form.training_type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-training-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Start New Training</DialogTitle>
          <DialogDescription>Position and training type together resolve the correct checklist.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2 space-y-2">
            <Label>Employee <span className="text-destructive">*</span></Label>
            {form.trainee_user_id ? (
              <div className="flex items-center justify-between rounded-lg border px-3 py-2 bg-muted/40" data-testid="selected-employee">
                <span className="flex items-center gap-2 text-sm"><Check size={15} className="text-success" /> {form.trainee_name}</span>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={clearEmp} data-testid="clear-employee"><X size={14} /></Button>
              </div>
            ) : showAddEmp ? (
              <div className="space-y-2 rounded-lg border p-3" data-testid="add-employee-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input placeholder="Full name" value={newEmp.name} onChange={(e) => setNewEmp({ ...newEmp, name: e.target.value })} data-testid="new-emp-name" />
                  <Input placeholder="name@purplelotuspatientcenter.com" value={newEmp.email} onChange={(e) => setNewEmp({ ...newEmp, email: e.target.value })} data-testid="new-emp-email" />
                  <Select value={newEmp.position} onValueChange={(v) => setNewEmp({ ...newEmp, position: v })}>
                    <SelectTrigger data-testid="new-emp-position"><SelectValue placeholder="Position" /></SelectTrigger>
                    <SelectContent>{settings?.positions?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={newEmp.location} onValueChange={(v) => setNewEmp({ ...newEmp, location: v })}>
                    <SelectTrigger data-testid="new-emp-location"><SelectValue placeholder="Location" /></SelectTrigger>
                    <SelectContent>{settings?.locations?.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" disabled={creatingEmp} onClick={createEmp} className="gap-1" data-testid="save-new-emp">{creatingEmp && <Loader2 size={14} className="animate-spin" />} Add & Select</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddEmp(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={15} />
                  <Input data-testid="employee-search-input" className="pl-9" placeholder="Search employee by name or email…" value={empQuery} onChange={(e) => setEmpQuery(e.target.value)} />
                </div>
                {empResults.length > 0 && (
                  <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border bg-popover shadow-sm" data-testid="employee-results">
                    {empResults.map((u) => (
                      <button type="button" key={u.user_id} onClick={() => selectEmp(u)} data-testid={`emp-option-${u.user_id}`} className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between">
                        <span>{u.name}</span>
                        <span className="text-muted-foreground text-xs">{u.position || u.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                <Button type="button" variant="ghost" size="sm" className="mt-1 gap-1 text-accent" onClick={() => setShowAddEmp(true)} data-testid="add-new-employee-button"><UserPlus size={14} /> Add new employee</Button>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Training Start Date <span className="text-destructive">*</span></Label>
            <Input type="date" data-testid="start-date-input" value={form.training_start_date} onChange={(e) => set("training_start_date", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Location</Label>
            <Select value={form.location} onValueChange={(v) => set("location", v)}>
              <SelectTrigger data-testid="location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
              <SelectContent>
                {settings?.locations?.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 space-y-2">
            <Label>Manager</Label>
            <Input data-testid="manager-input" value={form.manager} onChange={(e) => set("manager", e.target.value)} placeholder="Manager's name" />
          </div>
          <div className="space-y-2">
            <Label>Position <span className="text-destructive">*</span></Label>
            <Select value={form.trainee_position} onValueChange={(v) => set("trainee_position", v)}>
              <SelectTrigger data-testid="position-select"><SelectValue placeholder="Select position" /></SelectTrigger>
              <SelectContent>
                {settings?.positions?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Training Type <span className="text-destructive">*</span></Label>
            <Select value={form.training_type} onValueChange={(v) => set("training_type", v)}>
              <SelectTrigger data-testid="type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {settings?.training_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 space-y-2">
            <Label>Notes</Label>
            <Textarea data-testid="notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional context for this training…" rows={3} />
          </div>
        </div>

        {mutation.isError && (
          <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-lg p-3" data-testid="new-training-error">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{mutation.error?.response?.data?.detail || "Could not start training"}</span>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cancel-new-training">Cancel</Button>
          <Button disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()} data-testid="submit-new-training" className="gap-2">
            {mutation.isPending && <Loader2 size={16} className="animate-spin" />}
            Start & Open Checklist
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
